import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, Calculator, Archive, RefreshCw, Power, PowerOff, Settings } from 'lucide-react';
import EstimateurConfigDialog from '../components/EstimateurConfigDialog';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SimulateursManagement() {
  const { getAuthHeader } = useAuth();
  const [simulateurs, setSimulateurs] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSimulateur, setEditingSimulateur] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [configDialogSimulateur, setConfigDialogSimulateur] = useState(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    collection_id: '',
    is_active: true,
    config: {}
  });

  useEffect(() => {
    fetchSimulateurs();
    fetchCollections();
  }, [showArchived]);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/nomenclature-collections`, {
        headers: getAuthHeader()
      });
      setCollections(response.data.filter(c => c.is_active));
    } catch {
    }
  };

  const fetchSimulateurs = async () => {
    try {
      const response = await axios.get(
        `${API}/estimateurs?include_archived=${showArchived}`,
        { headers: getAuthHeader() }
      );
      setSimulateurs(response.data);
    } catch {
      toast.error('Erreur lors du chargement des simulateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSimulateur) {
        // Mise à jour
        await axios.put(
          `${API}/estimateurs/${editingSimulateur.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Simulateur mis à jour avec succès');
      } else {
        // Création
        await axios.post(
          `${API}/estimateurs`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Simulateur créé avec succès');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchSimulateurs();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(errorMsg);
    }
  };

  const handleEdit = (simulateur) => {
    setEditingSimulateur(simulateur);
    setFormData({
      name: simulateur.name,
      description: simulateur.description || '',
      code: simulateur.code || '',
      collection_id: simulateur.collection_id || '',
      is_active: simulateur.is_active,
      config: simulateur.config || {}
    });
    setIsDialogOpen(true);
  };

  const handleArchive = async (simulateurId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver cet simulateur ?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/estimateurs/${simulateurId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Simulateur archivé avec succès');
      fetchSimulateurs();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleHardDelete = async (simulateurId, simulateurName) => {
    const confirmation = window.prompt(
      `⚠️ ATTENTION : Cette action est irréversible !\n\nTapez "${simulateurName}" pour confirmer la suppression définitive :`
    );

    if (confirmation !== simulateurName) {
      if (confirmation !== null) {
        toast.error('Suppression annulée : le nom ne correspond pas');
      }
      return;
    }

    try {
      await axios.delete(
        `${API}/estimateurs/${simulateurId}/hard-delete`,
        { headers: getAuthHeader() }
      );
      toast.success('Simulateur supprimé définitivement');
      fetchSimulateurs();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openConfigDialog = (simulateur) => {
    setConfigDialogSimulateur(simulateur);
    setIsConfigDialogOpen(true);
  };

  const handleReactivate = async (simulateurId) => {
    try {
      await axios.put(
        `${API}/estimateurs/${simulateurId}/reactivate`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Simulateur réactivé avec succès');
      fetchSimulateurs();
    } catch {
      toast.error('Erreur lors de la réactivation');
    }
  };

  const toggleActiveStatus = async (simulateur) => {
    try {
      await axios.put(
        `${API}/estimateurs/${simulateur.id}`,
        { is_active: !simulateur.is_active },
        { headers: getAuthHeader() }
      );
      toast.success(`Simulateur ${simulateur.is_active ? 'désactivé' : 'activé'}`);
      fetchSimulateurs();
    } catch {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      code: '',
      collection_id: '',
      is_active: true,
      config: {}
    });
    setEditingSimulateur(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        
      {/* Bouton Retour */}
      <BackButton label="Retour" />

<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coppam-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Gestion des Simulateurs</h2>
          <p className="text-gray-600 mt-1">Créez et gérez vos différents simulateurs</p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={() => setShowArchived(!showArchived)}
            variant="outline"
            size="sm"
          >
            <Archive className="h-4 w-4 mr-2" />
            {showArchived ? 'Masquer archivés' : 'Afficher archivés'}
          </Button>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" />
                Nouvel Simulateur
              </Button>
            </DialogTrigger>
            
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>
                  {editingSimulateur ? 'Modifier l\'simulateur' : 'Nouvel simulateur'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nom du simulateur *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Simulateur BATICO, Simulateur Services..."
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="code">Code unique</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="Ex: BATICO, SERVICES..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Code unique pour identifier le simulateur</p>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description du simulateur..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="collection_id">Collection de nomenclatures associée</Label>
                  <select
                    id="collection_id"
                    value={formData.collection_id}
                    onChange={(e) => setFormData({ ...formData, collection_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Aucune collection</option>
                    {collections.map(collection => (
                      <option key={collection.id} value={collection.id}>
                        {collection.name} ({collection.nomenclatures_count} nomenclatures)
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Les nomenclatures de cette collection pourront être liées à cet simulateur
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="is_active"
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Simulateur actif</Label>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleDialogClose}>
                    Annuler
                  </Button>
                  <Button type="submit">
                    {editingSimulateur ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {simulateurs.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="pt-6">
              <div className="text-center text-gray-500 py-4 md:py-8">
                <Calculator className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Aucun simulateur trouvé</p>
                <p className="text-sm mt-2">Créez votre premier simulateur pour commencer</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          simulateurs.map((simulateur) => (
            <Card 
              key={simulateur.id} 
              className={`hover:shadow-lg transition-shadow ${simulateur.is_archived ? 'opacity-60' : ''}`}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Calculator className="h-6 w-6 text-blue-600" />
                      <CardTitle className="text-lg">{simulateur.name}</CardTitle>
                    </div>
                    {simulateur.code && (
                      <Badge className="bg-slate-700 text-white mb-2 font-semibold">
                        {simulateur.code}
                      </Badge>
                    )}
                    {simulateur.description && (
                      <p className="text-sm text-gray-600 mt-2">{simulateur.description}</p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge className={simulateur.is_active ? 'bg-green-700 text-white' : 'bg-gray-600 text-white'}>
                    {simulateur.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                  {simulateur.is_archived && (
                    <Badge className="bg-red-700 text-white font-semibold">Archivé</Badge>
                  )}
                  <Badge className="bg-slate-700 text-white font-semibold">
                    {simulateur.nomenclatures_count || 0} nomenclature(s)
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  {!simulateur.is_archived && (
                    <>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => openConfigDialog(simulateur)}
                          className="flex-1"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Configurer
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleActiveStatus(simulateur)}
                        >
                          {simulateur.is_active ? (
                            <PowerOff className="h-4 w-4" />
                          ) : (
                            <Power className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(simulateur)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleArchive(simulateur.id)}
                          className="flex-1 text-orange-600 hover:text-orange-700"
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          Archiver
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleHardDelete(simulateur.id, simulateur.name)}
                          className="flex-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer
                        </Button>
                      </div>
                    </>
                  )}
                  {simulateur.is_archived && (
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleReactivate(simulateur.id)}
                        className="flex-1"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Réactiver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleHardDelete(simulateur.id, simulateur.name)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Dialog de configuration */}
      <EstimateurConfigDialog
        simulateur={configDialogSimulateur}
        isOpen={isConfigDialogOpen}
        onClose={() => {
          setIsConfigDialogOpen(false);
          setConfigDialogSimulateur(null);
        }}
        onUpdate={fetchSimulateurs}
      />
    </div>
  );
}
